import * as React from "react";
import { useHistory } from "react-router-dom";
import { ButtonSize, CalendarIcon, Collapse, ExpandIcon, IconButton, Text, TextVariant } from "../../ui";
import { usePrefersReducedMotion } from "../../../hooks/usePrefersReducedMotion";
import { PartOrder } from "../../../generated/nest-graphql";
import { PartOrderCard } from "../PartOrderCard";
import { formatJobWindow, JobGrouping, PartsFilter } from "../partsStatus.utils";

/**
 * A job and every part order behind it — the "by job" grouping. Store grouping answers
 * "where do I drive"; this one answers "what does this job still need", which is the
 * question being asked when you arrive at the job rather than at a counter.
 */
export const JobGroup: React.FC<{
  group: JobGrouping;
  filter: PartsFilter;
  /** Arrived here from a "View Parts" link — ring it so it's findable on landing. */
  highlighted?: boolean;
  open: boolean;
  onToggle: () => void;
  onConfirmPickup: (order: PartOrder) => void;
  onReportIssue: (order: PartOrder) => void;
}> = ({ group, filter, highlighted, open, onToggle, onConfirmPickup, onReportIssue }) => {
  const history = useHistory();
  const orderCount = group.orders.length;
  const reduceMotion = usePrefersReducedMotion();
  const duration = reduceMotion ? 0 : 220;
  const scheduleLabel = formatJobWindow(group.start, group.end);

  return (
    <section
      id={`job-group-${group.key}`}
      className={`mb-4 border rounded-lg overflow-hidden ${
        highlighted ? "border-blue-400 ring-2 ring-blue-400/40" : "border-gray-700"
      }`}
    >
      {/* A div, not a button: the job number inside it is itself a link, and nesting
          interactive elements inside a button is invalid. Keeps the whole row as the
          collapse target so the tap area does not shrink on a phone. */}
      <div
        role="button"
        tabIndex={0}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 bg-gray-900 text-left cursor-pointer ${
          open ? "border-b border-gray-700" : ""
        }`}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onToggle();
          }
        }}
        aria-expanded={open}
      >
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <CalendarIcon style={{ fontSize: 18, color: "#94a3b8", flexShrink: 0 }} />
            <button
              type="button"
              disabled={!group.jobId}
              aria-label={group.jobId ? `Open job ${group.jobNumber ?? ""}` : undefined}
              // Stops the tap from also collapsing the group it sits inside.
              onClick={(event) => {
                event.stopPropagation();
                if (group.jobId) history.push(`/jobs/${group.jobId}`);
              }}
              className="bg-transparent border-0 p-0 m-0 text-left cursor-pointer disabled:cursor-default"
            >
              <Text variant={TextVariant.SUBTITLE1} style={{ fontWeight: 700, color: "inherit" }}>
                Job#{" "}
                <span style={{ textDecoration: group.jobId ? "underline" : "none" }}>{group.jobNumber ?? "—"}</span>
              </Text>
            </button>
          </span>
          {group.vehicle && (
            <Text variant={TextVariant.BODY2} className="text-gray-400">
              {group.vehicle}
            </Text>
          )}
          <Text variant={TextVariant.BODY2} className="text-gray-400">
            {scheduleLabel || "Not scheduled"}
          </Text>
        </span>
        <span className="flex items-center gap-1 flex-shrink-0">
          <span className="text-xs text-gray-400 whitespace-nowrap">
            {orderCount} {orderCount === 1 ? "order" : "orders"}
          </span>
          <IconButton size={ButtonSize.SMALL} as="span" tabIndex={-1} aria-hidden="true">
            <ExpandIcon
              style={{
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transition: `transform ${duration}ms ease`,
              }}
            />
          </IconButton>
        </span>
      </div>

      <Collapse open={open} durationMs={duration} unmountOnClose>
        <div className="flex flex-col gap-2 p-2 bg-[#080c14]">
          {group.orders.map((order) => (
            <PartOrderCard
              key={order.id}
              order={order}
              filter={filter}
              onConfirmPickup={onConfirmPickup}
              onReportIssue={onReportIssue}
            />
          ))}
        </div>
      </Collapse>
    </section>
  );
};
